import { apiFetch } from "../serverApi";
import React from "react";

import { Autocomplete, TextField, CircularProgress, Box } from "@mui/material";
import { ExpandMore, ChevronRight } from "@mui/icons-material";
import { useDispatch, useSelector } from 'react-redux';
import { baseUrl, listLayers } from "../Layers/layerBasics";
import {
    setPlace,
    setLocationName,
    setMainMenuOpen,
    setPoint,
    setClickPoint,
    setShowOverview,
    selectSelectedLayers,
    setSelectedLayers,
} from '../features/map/mapSlice';


//A search only runs once there is more than this many characters in the box
const minPrefix = 3;

//The part after a comma only narrows the results once it has this many characters
const minAreaPrefix = 3;

//The server truncates its reply to this many rows. A reply this size may be incomplete,
//so it cannot be narrowed locally - the narrowing must go to the server instead.
//(An object so tests can tune it.)
export const searchTuning = {serverMaxResults: 2000};

//Countries come back in this order, everything else is alphabetical
const countryOrder = ['England', 'Wales', 'Scotland', 'Northern Ireland'];

const compareCountry = (a, b) => {
    const posA = countryOrder.indexOf(a);
    const posB = countryOrder.indexOf(b);
    if (posA>=0 && posB>=0) {
        return posA - posB;
    }
    if (posA>=0) {
        return -1;
    }
    if (posB>=0) {
        return 1;
    }
    return a.localeCompare(b);
};

const blankToNull = (val) => ((val===null || val===undefined || val==='') ? null : val);

//Server rows, and the place history written by older versions of this component, both end up here
export const toPlace = (row) => ({
    name: row.name || row.placename,
    country: blankToNull(row.country),
    region: blankToNull(row.region),
    county: blankToNull(row.county),
    populated_place: blankToNull(row.populated_place),
    postcode_district: blankToNull(row.postcode_district),
    category: row.category || row.placetype,
    x: row.x,
    y: row.y,
});

/*
 * Splits the typed text at the first comma. The part before it is the name prefix sent
 * to the server; the part after it narrows the results by area - "Lewis Road, CR4",
 * "Narrow Street, London", "Canary, E14".
 */
export const parseSearch = (text) => {
    const raw = (text || '');
    const commaIdx = raw.indexOf(',');
    if (commaIdx<0) {
        return {namePart: raw.trim(), areaPart: ''};
    }
    return {namePart: raw.slice(0, commaIdx).trim(), areaPart: raw.slice(commaIdx+1).trim()};
};

//The fields the area part is matched against, most specific first
const areaFields = ['postcode_district', 'populated_place', 'county', 'region', 'country'];

//Names are matched lower case and without apostrophes or full stops, as the server
//(PlaceData2.searchKey) does, so "Queens Road" finds "Queen's Road"
export const nameKey = (s) => (s || '').toLowerCase().replace(/['\u2018\u2019.]/g, '');

//True when the place matches the name prefix and, if one was typed, the area prefix.
//Both prefixes must already be lower case.
export const matchesSearch = (place, namePrefix, areaPrefix) => {
    if (!nameKey(place.name).startsWith(nameKey(namePrefix))) {
        return false;
    }
    if (areaPrefix.length<minAreaPrefix) {
        return true;
    }
    return areaFields.some((field) => place[field] && place[field].toLowerCase().startsWith(areaPrefix));
};

//The area line shown under a country - the most specific geographic level that is set,
//with the postcode district alongside it, e.g. "Acton, W3" or "Suffolk, NR34"
const areaLabel = (place) => {
    const geo = place.populated_place || place.county || place.region;
    if (geo && place.postcode_district) {
        return geo+', '+place.postcode_district;
    }
    return geo || place.postcode_district || null;
};

//The grouping levels, outermost first: country, then the combined area line. A level with
//no value for a given place is skipped - that place hangs off its parent level instead.
const levels = [
    {name:'country', get:(place) => place.country, compare:compareCountry},
    {name:'area',    get:areaLabel,                compare:(a, b) => a.localeCompare(b)},
];

/*
 * Turns the flat list of places into the rows the listbox draws - one row per geographic
 * level heading and one row per place name, in the order they should appear. Each row
 * records the group keys it sits under so collapsing a heading can hide its subtree.
 */
const buildRows = (places) => {
    const rows = [];

    const walk = (items, levelIdx, depth, parentKey, parentLabel, ancestors) => {
        if (levelIdx>=levels.length) {
            items.slice().sort((a, b) => a.name.localeCompare(b.name))
                .forEach((place, idx) => rows.push({
                    rowType: 'place',
                    key: parentKey+'|'+place.name+'|'+idx,
                    depth: depth,
                    ancestors: ancestors,
                    place: place,
                }));
            return;
        }

        const level = levels[levelIdx];
        const groups = new Map();
        const ungrouped = [];
        items.forEach((place) => {
            const val = level.get(place);
            //A level that only repeats the one above it adds nothing - e.g. London/London/London
            if (val===null || val===parentLabel) {
                ungrouped.push(place);
            }
            else {
                if (!groups.has(val)) {
                    groups.set(val, []);
                }
                groups.get(val).push(place);
            }
        });

        [...groups.keys()].sort(level.compare).forEach((val) => {
            const key = parentKey+'|'+level.name+':'+val;
            rows.push({
                rowType: 'group',
                key: key,
                label: val,
                depth: depth,
                ancestors: ancestors,
            });
            walk(groups.get(val), levelIdx+1, depth+1, key, val, [...ancestors, key]);
        });

        //Places with nothing at this level carry on at the same depth, under the same parent
        if (ungrouped.length>0) {
            walk(ungrouped, levelIdx+1, depth, parentKey, parentLabel, ancestors);
        }
    };

    walk(places, 0, 0, '', null, []);

    //Drop any heading that ended up with no place names anywhere beneath it
    const live = new Set();
    rows.forEach((row) => {
        if (row.rowType==='place') {
            row.ancestors.forEach((key) => live.add(key));
        }
    });
    return rows.filter((row) => (row.rowType==='place' || live.has(row.key)));
};


//Asks the server for the places whose name starts with placename, optionally narrowed by
//area. Coordinates come back multiplied by a million and are scaled back here.
export async function fetchPlaces(placename, area) {
    const url = baseUrl+'/place2';
    let newPlaces = [];
    try {
        let res = await apiFetch(url, {
          mode:'cors',
          method: 'POST', // *GET, POST, PUT, DELETE, etc.
          credentials:'include',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
          },
          body: JSON.stringify({name:placename, area:area})
        });

        let data = await res.json();
        if (data && data.length) {
            for (let i  = 0 ; i<data.length; i++) {
                let plc = data[i];
                if (plc && plc.name) {
                    newPlaces.push({...toPlace(plc), x:plc.x/1000000, y:plc.y/1000000});
                }
            }
        }
    }
    catch (e) {
        console.log(e);
    }

    return newPlaces;
}

//Moves the map to a chosen place and opens its overview, closing the menu and any list layer
export const goToPlace = (dispatch, place, selectedLayers) => {
    dispatch(setClickPoint({x:null, y:null}));
    dispatch(setPoint({x: place.x, y: place.y}));
    dispatch(setPlace(place.name));
    // The category (null for a postcode) lets the overview name it (pickedPlaceName in nearPlace.js)
    dispatch(setLocationName({name:place.name, x:place.x, y:place.y, category:place.category || null}));
    dispatch(setMainMenuOpen(false));
    dispatch(setShowOverview(true));
    dispatch(setSelectedLayers(selectedLayers.filter((item)=>listLayers.indexOf(item)<0)));
};

export default function Places ({onSelectionComplete, width=250, height='200px', placeHistory, setPlaceHistory, label='Place', placeholder}) {

    const [open, setOpen] = React.useState(false);

    const selectedLayers = useSelector(selectSelectedLayers);

    const [loading, setLoading] = React.useState(false);
    const [inputVal, setInputval] = React.useState('');

    const [places, setPlaces] = React.useState([]);
    //Group keys the user has collapsed - everything starts expanded
    const [collapsed, setCollapsed] = React.useState(() => new Set());

    const dispatch = useDispatch();

    //Only the newest request may repaint the list - every older reply is dropped
    const requestId = React.useRef(0);
    //The prefix whose results are painted - collapse state survives repaints of the same prefix
    const paintedPrefix = React.useRef('');
    //Set while a heading click is being handled so the popup does not close for it
    const keepOpen = React.useRef(false);
    //The latest server reply, kept so retyping the area part or the same name re-narrows
    //in memory instead of re-fetching. Only a reply that passed the request guard lands here.
    //complete means the reply had no area filter and was not truncated, so it holds every
    //match for the name part and any area can be narrowed from it locally.
    const lastNameResponse = React.useRef({namePrefix:null, areaPrefix:null, complete:false, places:null});

    //Puts a result set on show. Collapse state is only reset when the prefix has
    //actually changed - a repaint of the same prefix keeps the user's collapses.
    const paint = (prefix, newPlaces) => {
        if (prefix!==paintedPrefix.current) {
            paintedPrefix.current = prefix;
            setCollapsed(new Set());
        }
        setPlaces(newPlaces);
    };

    const toggleGroup = (key) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            }
            else {
                next.add(key);
            }
            return next;
        });
    };

    const rows = React.useMemo(() => buildRows(places), [places]);

    //Hide everything sitting under a collapsed heading
    const options = React.useMemo(
        () => rows.filter((row) => !row.ancestors.some((key) => collapsed.has(key))),
        [rows, collapsed]);

    const historyPlaces = (namePrefix, areaPrefix) => {
        try {
            const jsonPlaceHistory = JSON.parse(placeHistory);
            if (!jsonPlaceHistory || !jsonPlaceHistory['places']) {
                return [];
            }
            return jsonPlaceHistory.places.map(toPlace).filter((place) => place.name)
                .filter((place) => matchesSearch(place, namePrefix, areaPrefix));
        }
        catch (e) {
            console.log(e);
            return [];
        }
    };

    /*
     * Called on every change to the typed text and on opening the popup. Short input shows
     * the place history; anything longer goes to the server. The rows already on show are
     * filtered against the new prefix at once, so nothing that fails to match what is in
     * the box is ever visible - not even while the request is still in flight.
     */
    const runSearch = (text) => {
        const id = ++requestId.current;
        const {namePart, areaPart} = parseSearch(text);
        const namePrefix = namePart.toLowerCase();
        const areaPrefix = areaPart.toLowerCase();
        //Collapse state is keyed on both halves of the search
        const paintKey = namePrefix+','+areaPrefix;

        if (namePart.length<=minPrefix) {
            setLoading(false);
            paint(paintKey, historyPlaces(namePrefix, areaPrefix));
            return;
        }

        //The area only reaches the server once it can narrow (3+ chars)
        const effectiveArea = areaPrefix.length>=minAreaPrefix ? areaPrefix : '';

        //The reply in hand answers this search when the name part matches and it either
        //holds the complete match set (narrow locally) or was fetched for this very area
        const cached = lastNameResponse.current;
        if (cached.namePrefix===namePrefix && (cached.complete || cached.areaPrefix===effectiveArea)) {
            setLoading(false);
            paint(paintKey, cached.places.filter((place) => matchesSearch(place, namePrefix, areaPrefix)));
            return;
        }

        setPlaces((prev) => prev.filter((place) => matchesSearch(place, namePrefix, areaPrefix)));
        setLoading(true);

        (async () => {
            const found = await fetchPlaces(namePart, effectiveArea);
            if (id===requestId.current) {
                lastNameResponse.current = {
                    namePrefix: namePrefix,
                    areaPrefix: effectiveArea,
                    complete: effectiveArea==='' && found.length<searchTuning.serverMaxResults,
                    places: found,
                };
                paint(paintKey, found.filter((place) => matchesSearch(place, namePrefix, areaPrefix)));
                setLoading(false);
            }
        })();
    };

    return (
      <Autocomplete
        id="Places"
        ListboxProps={{ style: { maxHeight: height } }}
        sx={{ width: width }}
        size="small"
        open={open}
        onOpen={() => {
          setOpen(true);
          runSearch(inputVal);
        }}
        onClose={() => {
          //A heading click reaches here as a selection - the popup stays open for it
          if (keepOpen.current) {
            keepOpen.current = false;
            return;
          }
          setOpen(false);
        }}
        value={null}
        getOptionLabel={(option) => (option.rowType==='group' ? option.label : option.place.name)}
        options={options}
        loading={loading}
        inputValue={inputVal}
        onInputChange = {(evt, newInputValue, reason) => {
            if (reason==='input') {
                setInputval(newInputValue);
                runSearch(newInputValue);
            }
            else if (reason==='clear') {
                requestId.current++;
                setLoading(false);
                setInputval('');
                paint(',', historyPlaces('', ''));
            }
        }}
        filterOptions={(x) => x}
        onChange = {(evt, newValue) => {
            if (newValue) {
                //Headings expand and collapse instead of being picked. Selection goes
                //through MUI's own machinery, so mouse and keyboard behave the same.
                if (newValue.rowType==='group') {
                    keepOpen.current = true;
                    toggleGroup(newValue.key);
                    return;
                }

                const place = newValue.place;
                setInputval(place.name);

                const jsonPlaceHistory = JSON.parse(placeHistory);
                let hasValue = false;
                for (let i = 0; i<jsonPlaceHistory.places.length; i++) {
                  const historic = toPlace(jsonPlaceHistory.places[i]);
                  if (historic.name===place.name && historic.x===place.x && historic.y===place.y) {
                    hasValue=true;
                    break;
                  }
                }

                if (!hasValue) {
                  jsonPlaceHistory.places.unshift(place);
                  const newPlaceHistory = JSON.stringify({places:jsonPlaceHistory.places.slice(0, Math.min(8, jsonPlaceHistory.places.length))});
                  setPlaceHistory(newPlaceHistory)
                }
                onSelectionComplete();
                goToPlace(dispatch, place, selectedLayers);
            }
        }}
        renderOption={(props, option) => {
            //MUI keys options by label, and labels repeat in this tree (two SE13 headings,
            //Lewisham as heading and leaf). Duplicate React keys corrupt the listbox DOM,
            //so rows are keyed by their unique path key instead.
            const {key, ...liProps} = props;
            const indent = 1 + option.depth*1.5;

            if (option.rowType==='group') {
              const isCollapsed = collapsed.has(option.key);
              return (
                <li key={option.key} {...liProps}>
                  <Box sx={{display:'flex', alignItems:'center', paddingLeft:indent-1, color:'text.secondary',
                    fontWeight:500, fontSize:'0.8125rem'}}>
                    {isCollapsed ? <ChevronRight fontSize="inherit"/> : <ExpandMore fontSize="inherit"/>}
                    <Box component="span" sx={{marginLeft:0.5}}>{option.label}</Box>
                  </Box>
                </li>
              );
            }

            return (
              <li key={option.key} {...liProps}>
                <Box sx={{paddingLeft:indent}}>
                  <span>{option.place.name}</span>
                  {option.place.category &&
                    <Box component="span" sx={{color:'text.secondary', marginLeft:0.75}}>({option.place.category})</Box>}
                </Box>
              </li>
            );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            inputProps={{...params.inputProps, 'aria-label': label || placeholder}}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={15} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    );
  }
